import * as React from 'react';
import { FormControl, InputGroup } from 'react-bootstrap';

import Tooltip from 'af-components/Tooltip';

import type { CellError } from '../../error.types';
import styles from '../styles.module.scss';

type FieldValue = string | number | undefined;

/**
 * RHFFieldProps are props coming from field in react-hook-form
 */
interface RHFFieldProps {
	name: string;
	value: FieldValue;
	onChange: React.ChangeEventHandler;
	onBlur: (event) => void;
}

interface Props extends RHFFieldProps {
	elementKey: string;
	placeholder?: string;
	className?: string;
	error?: CellError;
}

export const AVERAGE_CHAR_WIDTH = 8;
export const MIN_ROWS = 1;
export const MAX_ROWS = 10;
export const TEXT_AREA_LINE_HEIGHT = 18;

export const TextAreaCell: React.FC<Props> = React.memo((props) => {

	const {
		placeholder,
		name,
		elementKey,
		value,
		onChange,
		onBlur,
		className,
		error,
	} = props;

	const textAreaRef = React.useRef<HTMLTextAreaElement | null>(null);
	const hasAutoFocused = React.useRef(false);

	const [focused, setFocused] = React.useState(false);
	const [rows, setRows] = React.useState<number>(1);

	const id = React.useMemo(() => (
		`accqa__textarea__${elementKey}`
	), [elementKey]);

	const getClassName = React.useMemo(() => {
		let cn = className ?? `${styles.textarea}`;

		if (error) {
			cn = `${cn} ${styles['with-error']}`;
			if (error.type === 'tooltip') {
				cn = `${cn} ${styles['with-tooltip']}`;
			} else if (error.type === 'required') {
				cn = `${cn} ${styles['error-placeholder']}`;
			}
		}
		return cn;
	}, [className, error]);

	React.useEffect(() => {
		const textarea = document.getElementById(id);

		const lineHeight = TEXT_AREA_LINE_HEIGHT;
		const scrollHeight = textarea?.scrollHeight ?? 0;
		const numberOfRows = Math.floor(scrollHeight / lineHeight);
		if (numberOfRows !== rows) {
			setRows(numberOfRows);
		}
	}, [id, rows]);

	React.useEffect(() => {
		if (!hasAutoFocused.current) {
			const el = textAreaRef.current;
			if (el) {
				el.focus();
				const length = el.value.length;
				el.setSelectionRange(length, length);
			}
			hasAutoFocused.current = true;
			setFocused(true);
		}
	}, []);

	const handleFocus = React.useCallback(() => {
		setFocused(true);
	}, []);

	const handleBlur = React.useCallback((event) => {
		hasAutoFocused.current = false; // Reset for next time
		setFocused(false);
		onBlur(event);
	}, [onBlur]);

	const handleChange = React.useCallback((event) => {
		if (onChange) {
			onChange(event);
		}
		const previousRows = event.target.rows;
		event.target.rows = MIN_ROWS;

		const currentRows = Math.floor(event.target.scrollHeight / TEXT_AREA_LINE_HEIGHT);

		if (currentRows === previousRows) {
			event.target.rows = currentRows;
		}

		if (currentRows >= MAX_ROWS) {
			event.target.rows = MAX_ROWS;
			event.target.scrollTop = event.target.scrollHeight;
		}

		setRows(Math.min(currentRows, MAX_ROWS));
	}, [onChange]);

	const renderCell = React.useMemo(() => {
		const placeholderInCell = error?.type === 'required'
			? error?.message
			: placeholder
				? placeholder
				: '-';

		return (
			<InputGroup className={`${styles['textarea-wrapper']}`}>
				<FormControl
					as="textarea"
					className={getClassName}
					id={id}
					key={elementKey}
					name={name}
					onBlurCapture={handleBlur}
					onChange={handleChange}
					onFocus={handleFocus}
					onMouseEnter={handleChange}
					placeholder={!focused ? placeholderInCell : undefined}
					ref={textAreaRef}
					rows={rows}
					spellCheck="false"
					value={value}

				/>
				{error?.type === 'tooltip' && error.message &&
					<Tooltip message={error.message} placement="auto">
						<span className="icon-notification_status" />
					</Tooltip>
				}
			</InputGroup>
		);
	}, [error, rows, placeholder, getClassName, id, elementKey, name, handleBlur, handleChange, handleFocus, focused, value]);

	return renderCell;
});

